import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, ShieldCheck, ArrowRight, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Post-reserve landing page (deposit-free model).
 *
 * Reservation is submitted → status `pending_review`. Compliance reviews within
 * 24h. Once approved, buyer gets an email + prompt to top up their wallet.
 */
export default function ReserveSuccess() {
  const [params] = useSearchParams();
  const reservationId = params.get("reservation_id") || sessionStorage.getItem("connexis_reservation");
  const navigate = useNavigate();

  return (
    <div className="min-h-[70vh] grid place-items-center px-4 py-20">
      <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white shadow-soft p-8" data-testid="reserve-success-card">
        <div className="text-center space-y-5" data-testid="reserve-status-received">
          <div className="mx-auto inline-grid place-items-center size-14 rounded-2xl bg-[#0F172A] text-white">
            <CheckCircle2 className="size-6" strokeWidth={1.8} />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">Reservation received ✓</h1>
            <p className="mt-2 text-sm text-slate-600">
              Thanks — your campaign is in our compliance review queue. We'll get back to you within 24 hours.
              No payment is needed yet.
            </p>
            {reservationId && (
              <p className="mt-3 text-[11px] font-mono text-slate-400" data-testid="reserve-ref">
                Ref: {reservationId.slice(0, 8)}…
              </p>
            )}
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 text-left space-y-3 text-sm">
            <Step Icon={ShieldCheck} title="Compliance review" body="We check sector compliance + lead-quality fit. Average 4 hours during business hours." />
            <Step Icon={Wallet} title="Top up your wallet" body="When approved, we'll email you to top up via card or bank transfer. Leads start flowing immediately after." />
          </div>

          <Button onClick={() => navigate("/dashboard")} className="rounded-full w-full bg-[#0055FF] hover:bg-[#0044CC]" data-testid="reserve-dashboard-cta">
            Go to dashboard <ArrowRight className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function Step({ Icon, title, body }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="size-4 text-[#0055FF] mt-0.5 shrink-0" strokeWidth={1.7} />
      <div>
        <div className="font-medium text-slate-900 text-[13px]">{title}</div>
        <div className="text-slate-600 text-[12.5px] mt-0.5">{body}</div>
      </div>
    </div>
  );
}
